import React, { useEffect, useState, useRef } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import config from "../../config";
import makeRequest from "../../makeRequest";

/* ─────────────────────── CSS keyframes (injected once) ─────────────── */
const STYLE_ID = "team-page-animations";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes teamFadeUp {
      from { opacity: 0; transform: translateY(40px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes teamPulse {
      0%, 100% { opacity: 0.4; }
      50%      { opacity: 1; }
    }
    @keyframes teamShimmer {
      0%   { background-position: -400px 0; }
      100% { background-position: 400px 0; }
    }

    .team-members-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 35px;
    }

    @media (max-width: 991px) {
      .team-members-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 24px;
      }
    }

    @media (max-width: 640px) {
      .team-members-grid {
        grid-template-columns: 1fr;
        gap: 20px;
      }
    }

    /* Override swiper-specific offsets that break grid layout */
    .team-members-grid .it-team-item {
      overflow: hidden;
      border-radius: 10px;
      background: #fff;
      box-shadow: 0 4px 25px rgba(8, 87, 222, 0.12);
      transition: transform 0.3s ease, box-shadow 0.3s ease;
    }
    .team-members-grid .it-team-item:hover {
      transform: translateY(-6px);
      box-shadow: 0 12px 32px rgba(8, 87, 222, 0.18);
    }
    .team-members-grid .it-team-thumb {
      border-radius: 0;
      overflow: hidden;
    }
    .team-members-grid .it-team-thumb img {
      border-radius: 0;
      width: 100%;
      aspect-ratio: 3 / 4;
      object-fit: cover;
      object-position: top center;
      display: block;
      transition: transform 1.3s ease;
    }
    .team-members-grid .it-team-item:hover .it-team-thumb img {
      transform: scale(1.06);
    }
    .team-members-grid .it-team-content {
      margin-right: 0 !important;
      margin-top: 0 !important;
      padding: 28px 20px 24px !important;
      box-shadow: none !important;
      border-radius: 0 !important;
    }
  `;
  document.head.appendChild(style);
}

/* ─────────────────────── palette per section ───────────────────────── */
const PALETTES = [
  { accent: "#1b74bb", dark: "#0d3b6e", from: "#0a1628", light: "#edf5fc" },
  { accent: "#0d6e6e", dark: "#094d4d", from: "#042424", light: "#e6f5f5" },
  { accent: "#6d28d9", dark: "#4c1d95", from: "#1a0636", light: "#f0eaff" },
  { accent: "#b45309", dark: "#7c2d12", from: "#2d1708", light: "#fef3c7" },
  { accent: "#1a5276", dark: "#0e3455", from: "#091d30", light: "#e4f0f6" },
];

/* ─────────────────────── Image hook (fetch blob → objectURL) ────── */
function useBlobImage(memberId) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const baseUrl = config.api.baseUrl;

  useEffect(() => {
    let revoke = null;
    setLoading(true);
    fetch(`${baseUrl}/website/member/${memberId}/image`)
      .then((r) => {
        if (!r.ok) throw new Error("no image");
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        revoke = url;
        setSrc(url);
      })
      .catch(() => setSrc(null))
      .finally(() => setLoading(false));

    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [memberId, baseUrl]);

  return { src, loading };
}

/* ─────────────────────── Initials fallback ──────────────────────── */
function Initials({ name, accent }) {
  const letters = name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(145deg, ${accent}, ${accent}cc)`,
        fontSize: 40,
        fontWeight: 800,
        color: "#fff",
        letterSpacing: 3,
      }}
    >
      {letters}
    </div>
  );
}

/* ─────────────────────── Member Card ────────────────────────────── */
function MemberCard({ member, palette, delay }) {
  const { src, loading: imgLoading } = useBlobImage(member.id);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        animation: visible ? `teamFadeUp 0.6s ${delay}s ease both` : "none",
      }}
    >
      <div className="it-team-item">
        <div className="it-team-thumb" style={{ position: "relative", background: palette.light }}>
          {imgLoading ? (
            <div
              style={{
                width: "100%",
                paddingBottom: "112%",
                background: `linear-gradient(90deg, ${palette.light} 0%, #fff 50%, ${palette.light} 100%)`,
                backgroundSize: "800px 100%",
                animation: "teamShimmer 1.6s infinite linear",
              }}
            />
          ) : src ? (
            <img
              src={src}
              alt={member.name}
              style={{ width: "100%", aspectRatio: "3 / 4", objectFit: "cover", objectPosition: "top center" }}
            />
          ) : (
            <div style={{ width: "100%", aspectRatio: "3 / 4" }}>
              <Initials name={member.name} accent={palette.accent} />
            </div>
          )}
        </div>

        <div className="it-team-content text-center">
          <div className="it-team-author-box">
            <h4 className="it-team-title" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", padding: "0 20px" }}>
              <a className="hover-anim tertiary" href="/team-details" title={member.name}>
                {member.name}
              </a>
            </h4>
            <span>{member.designation || "Consultant"}</span>
          </div>

          <div className="it-team-social">
            <a href="/team-details" aria-label={`${member.name} Facebook`}>
              <i className="flaticon-facebook-app-symbol"></i>
            </a>
            <a href="/team-details" aria-label={`${member.name} YouTube`}>
              <i className="flaticon-youtube"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Section Header ─────────────────────────── */
function SectionHeader({ title, description, palette, count }) {
  return (
    <div style={{ textAlign: "center", marginBottom: 48 }}>
      {/* pill badge */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          background: palette.light,
          border: `1.5px solid ${palette.accent}30`,
          borderRadius: 50,
          padding: "6px 20px 6px 8px",
          marginBottom: 18,
        }}
      >
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: palette.accent,
            color: "#fff",
            fontSize: 12,
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {count}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 2.5,
            textTransform: "uppercase",
            color: palette.accent,
          }}
        >
          Team
        </span>
      </div>

      <h2
        style={{
          margin: "0 0 14px",
          fontSize: "clamp(24px, 3.5vw, 38px)",
          fontWeight: 800,
          color: "#0f172a",
          lineHeight: 1.2,
        }}
      >
        {title}
      </h2>

      {/* underline */}
      <div style={{ display: "flex", justifyContent: "center", gap: 5, marginBottom: description ? 18 : 0 }}>
        <div style={{ width: 44, height: 4, borderRadius: 2, background: palette.accent }} />
        <div style={{ width: 12, height: 4, borderRadius: 2, background: `${palette.accent}80` }} />
        <div style={{ width: 5, height: 4, borderRadius: 2, background: `${palette.accent}30` }} />
      </div>

      {description && <p style={{ margin: "0 auto", maxWidth: 600, fontSize: 15, color: "#64748b", lineHeight: 1.8 }}>{description}</p>}
    </div>
  );
}

/* ─────────────────────── Skeleton ────────────────────────────────── */
function SkeletonCard() {
  return (
    <div className="it-team-item">
      <div
        style={{
          width: "100%",
          paddingBottom: "112%",
          background: "linear-gradient(90deg, #e2e8f0, #f8fafc, #e2e8f0)",
          backgroundSize: "800px 100%",
          animation: "teamShimmer 1.6s infinite linear",
          borderRadius: 10,
        }}
      />
      <div className="it-team-content text-center">
        <div className="it-team-author-box" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 20 }}>
          <div style={{ height: 20, width: "70%", background: "#e2e8f0", borderRadius: 6, margin: "0 auto 10px" }} />
          <div style={{ height: 14, width: "40%", background: "#f1f5f9", borderRadius: 6, margin: "0 auto" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 24 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ width: 35, height: 35, borderRadius: "50%", background: "#e2e8f0" }} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ MAIN ═══════════════════════════════════════ */
const TeamMain = ({ asSection = false }) => {
  const Tag = asSection ? "div" : "main";
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    makeRequest({ route: "/website/team/public", method: "get" })
      .then((res) => setTeams(res?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <Tag>
      {!asSection && <Breadcrumb title="Our Team" subTitle="Our Team" />}

      {/* ═══ Team sections ══════════════════════════════════════════ */}
      <div style={{ background: "#f8fafc" }}>
        {loading ? (
          <div style={{ maxWidth: 1240, margin: "0 auto", padding: "80px 28px" }}>
            {[0, 1].map((s) => (
              <div key={s} style={{ marginBottom: 80 }}>
                <div style={{ width: 200, height: 36, background: "#e2e8f0", borderRadius: 50, margin: "0 auto 56px" }} />
                <div className="team-members-grid">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : teams.length === 0 ? (
          <div style={{ textAlign: "center", padding: "100px 24px", color: "#94a3b8" }}>
            <div style={{ fontSize: 60, marginBottom: 16 }}>👥</div>
            <p style={{ fontSize: 18 }}>No teams available at the moment.</p>
          </div>
        ) : (
          teams.map((team, idx) => {
            const palette = PALETTES[idx % PALETTES.length];
            const members = team.teamMembers || [];
            return (
              <section
                key={team.id}
                style={{
                  background: idx % 2 === 0 ? "#f8fafc" : "#ffffff",
                  padding: "80px 0",
                }}
              >
                <div style={{ maxWidth: 1260, margin: "0 auto", padding: "0 28px" }}>
                  <SectionHeader title={team.name} description={team.description} palette={palette} count={members.length} />

                  {members.length === 0 ? (
                    <p style={{ textAlign: "center", color: "#94a3b8", fontSize: 14 }}>No members assigned to this team yet.</p>
                  ) : (
                    <div className="team-members-grid">
                      {members.map((member, mIdx) => (
                        <MemberCard key={member.id} member={member} palette={palette} delay={mIdx * 0.08} />
                      ))}
                    </div>
                  )}
                </div>
              </section>
            );
          })
        )}

        {/* ═══ CTA ══════════════════════════════════════════════════ */}
        {!loading && teams.length > 0 && (
          <div
            style={{
              background: "linear-gradient(135deg, #0a1628, #1b74bb)",
              padding: "64px 40px",
              textAlign: "center",
            }}
          >
            <h3 style={{ color: "#fff", fontSize: "clamp(20px, 2.5vw, 30px)", fontWeight: 800, margin: "0 0 12px" }}>
              Ready to Experience World-Class Care?
            </h3>
            <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 15, margin: "0 0 30px" }}>
              Schedule an appointment with our expert medical team today.
            </p>
            <a
              href="/appointment"
              style={{
                display: "inline-block",
                background: "#fff",
                color: "#1b74bb",
                fontWeight: 700,
                fontSize: 14,
                padding: "14px 38px",
                borderRadius: 50,
                textDecoration: "none",
                boxShadow: "0 6px 24px rgba(0,0,0,0.2)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.boxShadow = "0 10px 32px rgba(0,0,0,0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.2)";
              }}
            >
              Book an Appointment
            </a>
          </div>
        )}
      </div>
    </Tag>
  );
};

export default TeamMain;
